import React from "react";

// All prices come from Site Content; set every render via setMountConfig(content).
let CFG = { flatValue: [0, 0, 0], fullMotion: [0, 0, 0], cards: {}, addons: {} };
export const setMountConfig = (content) => {
  CFG = { ...content.mounts, addons: content.addons || {} };
};
const n = (v) => Number(v) || 0;
const money = (v) => `$${v.toLocaleString()}`;

export const MOUNTS = [
  { k: "own", name: "Customer's own" },
  { k: "flat", name: "Flat Mount", free: true },
  { k: "tilt", name: "Tilt Mount", free: true },
  { k: "full", name: "Full-Motion Mount", upgrade: true },
  { k: "spec", name: "Specialty", specialty: true },
];
export const SPECIALTY = ["Ceiling", "Motorized", "Fireplace pull-down", "Electric"];
export const SPEC_NOTE = "Price confirmed in your custom quote.";
const card = (x) => ({ ...x, label: CFG.cards?.[x.k]?.label || x.name, sub: CFG.cards?.[x.k]?.sub || "", img: CFG.cards?.[x.k]?.src || "" });

// TV is priced instantly (not Gallery, not over 100 inches).
export const isCustomSize = (t) => !!t.size?.custom && !t.type?.gallery;
export const usesMount = (t) => !!t.type && !t.type.gallery && !isCustomSize(t);
export const mountReady = (t) => !!t.mount && (t.mount.specialty ? !!t.specialty : true);

export const mountAmt = (t) => {
  if (!usesMount(t) || !t.mount || t.size == null) return 0;
  const i = t.size.idx;
  if (t.mount.k === "own") return -n(CFG.flatValue[i]);
  if (t.mount.upgrade) return n(CFG.fullMotion[i]);
  return 0;
};

export const mountLine = (t) => {
  if (!usesMount(t) || !mountReady(t)) return null;
  const i = t.size?.idx ?? 0;
  if (t.mount.k === "own") return { label: `Customer's own mount, included mount value deducted ${money(n(CFG.flatValue[i]))}`, amount: `-${money(n(CFG.flatValue[i]))}` };
  if (t.mount.specialty) return { label: `Specialty mount (${t.specialty}): ${SPEC_NOTE.replace(/\.$/, "")}`, amount: "Custom quote" };
  if (t.mount.upgrade) return { label: "Full-Motion Mount upgrade", amount: `+${money(mountAmt(t))}` };
  return { label: `${t.mount.name}, included${t.bundle ? " (free with your TV bundle)" : ""}`, amount: "Included" };
};

export const mountPayload = (t) => {
  if (!usesMount(t) || !mountReady(t)) return [];
  const amt = mountAmt(t);
  const own = t.mount.k === "own";
  const spec = !!t.mount.specialty;
  const type = spec ? `specialty (${t.specialty})` : own ? "customer own mount" : t.mount.k === "full" ? "full-motion" : t.mount.k;
  const amountText = own ? `included mount value deducted -${money(-amt)}` : spec ? "0 (specialty, price in custom quote)" : amt === 0 ? "0 (included in tier price)" : `full-motion upgrade +${money(amt)}`;
  return [
    `Mount: ${mountLine(t).label}`,
    `Mount details: type=${type}; full-motion upgrade=${t.mount.upgrade ? `yes +${money(amt)}` : "no"}; amount=${amountText}; size band=${t.size?.label}; already has mount=${own ? "yes" : "no"}; specialty=${spec ? "yes" : "no"}`,
  ];
};

// Wall add-ons and concealment
export const WALL_ADDON = { brick: "brick", tile: "tile", stone: "stone" };
export const wallAmt = (t) => (isCustomSize(t) || !t.wall?.addon ? 0 : n(CFG.addons[t.wall.addon]));
export const fireplaceAmt = (t) => (isCustomSize(t) || !t.fireplace ? 0 : n(CFG.addons.fireplace));
export const isMasonry = (t) => ["brick", "stone"].includes(t.wall?.addon);
export const CONCEAL = [
  { k: "channel", label: "Surface channeling", sub: "A surface raceway that hides power, HDMI, and optical cables." },
  { k: "inwall", label: "In-wall hiding", sub: "Low-voltage cables routed inside the wall only, no power move." },
  { k: "powerkit", label: "Power kit", sub: "Standard single-outlet kit plus 30 minute install, for an outlet below the TV. Drywall only. Bays blocked by fireblocking, studs, or plumbing go to custom quote." },
];
export const concealAvail = (t) => CONCEAL.filter((c) => {
  if (t.type?.gallery) return true;
  if (c.k === "inwall") return !isMasonry(t);
  if (c.k === "powerkit") return t.wall?.drywall && t.outlet?.k === "below";
  return true;
});
export const concealChosen = (t) => concealAvail(t).filter((c) => (t.conceal || []).includes(c.k));
export const concealAmt = (t) => (isCustomSize(t) || t.type?.gallery ? 0 : concealChosen(t).reduce((s, c) => s + n(CFG.addons[c.k]), 0));
export const FRAME_CONCEAL_NOTE = "Wire hiding is included with your Frame TV install. The installer confirms the method on site: in-wall hiding or the power kit is preferred because it handles the One Connect box, and surface channeling is used only if there is absolutely no other way.";
export const addonPrice = (k) => n(CFG.addons[k]);

const priceText = (m, i) => {
  if (m.k === "own") return `Included mount value deducted, -${money(n(CFG.flatValue[i]))}`;
  if (m.specialty) return SPEC_NOTE;
  if (m.upgrade) return `Upgrade +${money(n(CFG.fullMotion[i]))}`;
  return "Included, $0 additional";
};

const Card = ({ active, onClick, testid, children }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex flex-col justify-start text-left rounded-2xl border-2 transition-all overflow-hidden bg-white ${active ? "border-[#3b9dff] ring-2 ring-[#3b9dff]/30 shadow-lg" : "border-slate-200 hover:border-[#3b9dff]/50"}`}>
    {children}
  </button>
);

export const MountStep = ({ cur, setCur }) => {
  const i = cur.size?.idx ?? 0;
  const m = cur.mount;
  return (
    <div data-testid="step-mount">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MOUNTS.map(card).map((x) => (
          <Card key={x.k} active={m?.k === x.k} testid={`mount-${x.k}`}
            onClick={() => {
              setCur({ mount: x });
              if (x.specialty && window.innerWidth < 1024) setTimeout(() => document.getElementById("mount-options")?.scrollIntoView({ behavior: "smooth", block: "center" }), 320);
            }}>
            <div className="w-full aspect-video bg-slate-100 overflow-hidden"><img src={x.img} alt={x.label} className="w-full h-full object-cover" loading="eager" decoding="async" /></div>
            <div className="p-4">
              <p className="font-bold text-[#0a1f44]">{x.label}</p>
              <p className="text-xs text-slate-500 mt-1">{x.sub}</p>
              <p className={`text-xs font-bold mt-2 ${x.k === "own" ? "text-green-600" : x.specialty ? "text-orange-600" : "text-[#3b9dff]"}`} data-testid={`mount-${x.k}-price`}>{priceText(x, i)}</p>
            </div>
          </Card>
        ))}
      </div>
      <div className={`grid transition-all duration-300 ${m?.specialty ? "grid-rows-[1fr] opacity-100 mt-6" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden" id="mount-options">
          {m?.specialty && (
            <div data-testid="mount-specialty">
              <p className="font-bold text-[#0a1f44] mb-3">Which specialty mount?</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
                {SPECIALTY.map((s, k) => (
                  <Card key={s} active={cur.specialty === s} onClick={() => setCur({ specialty: s })} testid={`mount-specialty-${k}`}>
                    <div className="p-4"><p className="font-bold text-[#0a1f44]">{s}</p><p className="text-xs text-orange-600 font-semibold mt-1">{SPEC_NOTE}</p></div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <p className="mt-5 text-xs text-slate-500">Flat Mount and Tilt Mount are included in your install price. Exact mount model is confirmed in your itemized quote.</p>
    </div>
  );
};
